import React, { useState, useEffect } from 'react';
import { Building2, Plus, Pencil, Trash2, Search, X, Check, Loader2, ChevronDown, ChevronRight, Upload, FileText, Users, ExternalLink, Key, Scale } from 'lucide-react';
import { useAuth } from '../contexts/TestAuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface LawFirmClient {
  id: string;
  claimant_name: string;
  business_name: string;
  claimant_email: string;
  claimant_phone: string;
  event_type?: string;
  event_date?: string;
  status: string;
  created_at: string;
  claim_amount?: number;
  source?: string;
}

interface LawFirm {
  id: string;
  name: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  cases_count: number;
  created_at: string;
  clients?: LawFirmClient[];
  clients_total?: number;
  attorney_password_set?: boolean;
}

export default function LawFirmManagement() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [lawFirms, setLawFirms] = useState<LawFirm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState<LawFirm | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [expandedFirm, setExpandedFirm] = useState<string | null>(null);
  const [loadingCases, setLoadingCases] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  // Bulk import state
  const [bulkImportFirm, setBulkImportFirm] = useState<LawFirm | null>(null);
  const [bulkImportData, setBulkImportData] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);

  // Attorney password state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordFirm, setPasswordFirm] = useState<LawFirm | null>(null);
  const [attorneyPassword, setAttorneyPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch law firms
  useEffect(() => {
    fetchLawFirms();
  }, []);

  const fetchLawFirms = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const data = await api.get('/api/v1/law-firms', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setLawFirms(data.law_firms || []);
    } catch (error) {
      console.error('Error fetching law firms:', error);
      // Mock data fallback
      setLawFirms([
        { id: '1', name: 'Anderson & Associates', contact_name: 'John Anderson', contact_email: 'john@anderson.com', cases_count: 12, created_at: '2024-06-15' },
        { id: '2', name: 'Baker Legal Group', contact_name: 'Sarah Baker', contact_email: 'sarah@bakerlegal.com', cases_count: 8, created_at: '2024-08-20' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsForFirm = async (firmId: string, firmName: string) => {
    setLoadingCases(firmName);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/law-firms/${firmId}/clients?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      // Update the firm with its clients
      setLawFirms(prev => prev.map(f => 
        f.id === firmId 
          ? { ...f, clients: data.clients || [], clients_total: data.total || 0 }
          : f
      ));
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingCases(null);
    }
  };

  const handleExpandFirm = async (firm: LawFirm) => {
    if (expandedFirm === firm.id) {
      setExpandedFirm(null);
    } else {
      setExpandedFirm(firm.id);
      if (!firm.clients) {
        await fetchClientsForFirm(firm.id, firm.name);
      }
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setValidationError('Law firm name is required');
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingFirm) {
        await api.put(`/api/v1/law-firms/${editingFirm.id}`, formData, { headers });
      } else {
        await api.post('/api/v1/law-firms', formData, { headers });
      }
      
      await fetchLawFirms();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving law firm:', error);
      // Add locally on error
      if (!editingFirm) {
        const newFirm: LawFirm = {
          id: Date.now().toString(),
          ...formData,
          cases_count: 0,
          created_at: new Date().toISOString().split('T')[0]
        };
        setLawFirms(prev => [...prev, newFirm]);
      }
      handleCloseModal();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = await getToken();
      await api.delete(`/api/v1/law-firms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error deleting:', error);
    }
    setLawFirms(prev => prev.filter(f => f.id !== id));
    setDeleteConfirm(null);
  };

  const handleBulkImport = async () => {
    if (!bulkImportFirm || !bulkImportData.trim()) return;
    
    setBulkImporting(true);
    try {
      // Parse CSV data: claimant_name, claimant_email, claimant_phone, business_name, business_address, event, event_date
      const lines = bulkImportData.trim().split('\n');
      const clients = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return {
          claimant_name: parts[0] || '',
          claimant_email: parts[1] || '',
          claimant_phone: parts[2] || '',
          business_name: parts[3] || '',
          business_address: parts[4] || '',
          event: parts[5] || '',
          event_date: parts[6] || ''
        };
      }).filter(c => c.claimant_name); // Filter out empty rows

      const token = await getToken();
      const result = await api.post(
        `/api/v1/law-firms/${bulkImportFirm.id}/bulk-import`,
        { clients },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Successfully imported ${result.imported} clients for ${bulkImportFirm.name}!${result.errors?.length > 0 ? ` ${result.errors.length} errors occurred.` : ''}`);
      await fetchLawFirms();
      // Refresh the expanded firm's clients if it was the one we imported to
      if (expandedFirm === bulkImportFirm.id) {
        await fetchClientsForFirm(bulkImportFirm.id, bulkImportFirm.name);
      }
      
      setShowBulkImportModal(false);
      setBulkImportData('');
      setBulkImportFirm(null);
    } catch (error: any) {
      console.error('Error bulk importing:', error);
      setValidationError(error.message || 'Error importing clients. Please try again.');
    } finally {
      setBulkImporting(false);
    }
  };

  // Set attorney password for law firm
  const handleSetAttorneyPassword = async () => {
    if (!passwordFirm || !attorneyPassword) return;
    
    try {
      setSettingPassword(true);
      const token = await getToken();
      await api.post(
        `/api/v1/attorney/admin/law-firms/${passwordFirm.id}/set-password`,
        { password: attorneyPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage(`Attorney portal access set for ${passwordFirm.name}! Login: ${passwordFirm.contact_email}`);
      setShowPasswordModal(false);
      setPasswordFirm(null);
      setAttorneyPassword('');
      await fetchLawFirms();
    } catch (error: any) {
      console.error('Error setting attorney password:', error);
      setValidationError(error.message || 'Error setting password. Please try again.');
    } finally {
      setSettingPassword(false);
    }
  };

  const openPasswordModal = (firm: LawFirm) => {
    setPasswordFirm(firm);
    setAttorneyPassword('');
    setShowPasswordModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingFirm(null);
    setFormData({
      name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: ''
    });
  };

  const handleEdit = (firm: LawFirm) => {
    setEditingFirm(firm);
    setFormData({
      name: firm.name,
      contact_name: firm.contact_name || '',
      contact_email: firm.contact_email || '',
      contact_phone: firm.contact_phone || '',
      address: firm.address || ''
    });
    setShowAddModal(true);
  };

  const openBulkImport = (firm: LawFirm) => {
    setBulkImportFirm(firm);
    setShowBulkImportModal(true);
  };

  const filteredFirms = lawFirms.filter(firm =>
    firm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    firm.contact_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { bg: string; text: string }> = {
      completed: { bg: 'bg-green-100', text: 'text-green-700' },
      draft: { bg: 'bg-amber-100', text: 'text-amber-700' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-700' },
    };
    const config = configs[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-bold text-white">Law Firm Management</h2>
              <p className="text-sm text-blue-100">Manage your referring law firms and their clients</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-blue-700 rounded-lg font-semibold text-sm hover:bg-blue-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Law Firm
          </button>
        </div>
      </div>

      <div className="p-6 bg-white rounded-b-xl border border-t-0 border-gray-200">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search law firms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Law Firms List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading law firms...</span>
          </div>
        ) : filteredFirms.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No law firms found</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              Add Your First Law Firm
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFirms.map((firm) => (
              <div key={firm.id} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Firm Row */}
                <div
                  className={`flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors ${expandedFirm === firm.id ? 'border-b border-gray-200' : ''}`}
                  onClick={() => handleExpandFirm(firm)}
                >
                  <div className="flex items-center gap-4">
                    <button className="p-1 hover:bg-gray-200 rounded">
                      {expandedFirm === firm.id ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{firm.name}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        {firm.contact_name && <span>{firm.contact_name}</span>}
                        {firm.contact_email && (
                          <span className="text-blue-600">{firm.contact_email}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right mr-4">
                      <p className="text-2xl font-bold text-gray-900">{firm.cases_count}</p>
                      <p className="text-xs text-gray-500">cases</p>
                    </div>
                    
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openBulkImport(firm)}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Bulk Import Clients"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openPasswordModal(firm)}
                        className={`p-2 rounded-lg transition-colors ${
                          firm.attorney_password_set 
                            ? 'text-green-500 hover:text-green-600 hover:bg-green-50' 
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                        title={firm.attorney_password_set ? "Update Attorney Portal Access" : "Set Attorney Portal Access"}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(firm)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      
                      {deleteConfirm === firm.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(firm.id)}
                            className="p-2 text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                            title="Confirm delete"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(firm.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                          disabled={firm.cases_count > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Clients */}
                {expandedFirm === firm.id && (
                  <div className="p-4 bg-white">
                    {loadingCases === firm.name ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                        <span className="ml-2 text-sm text-gray-500">Loading clients...</span>
                      </div>
                    ) : firm.clients && firm.clients.length > 0 ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Clients ({firm.clients_total || firm.clients.length})
                          </h4>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); openBulkImport(firm); }}
                              className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                            >
                              <Upload className="w-3 h-3" /> Import More
                            </button>
                            <button
                              onClick={() => navigate(`/dashboard?law_firm=${encodeURIComponent(firm.name)}`)}
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                              View All <ExternalLink className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500 uppercase border-b">
                              <th className="pb-2 font-medium">Claimant</th>
                              <th className="pb-2 font-medium">Business</th>
                              <th className="pb-2 font-medium">Contact</th>
                              <th className="pb-2 font-medium">Event</th>
                              <th className="pb-2 font-medium">Status</th>
                              <th className="pb-2 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {firm.clients.slice(0, 15).map((c) => (
                              <tr 
                                key={c.id} 
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => c.status === 'draft' 
                                  ? navigate(`/wizard-v2?draftId=${c.id}`)
                                  : navigate(`/report/${c.id}`)
                                }
                              >
                                <td className="py-2">
                                  <div className="font-medium text-gray-900">{c.claimant_name || '-'}</div>
                                  {c.claimant_email && (
                                    <div className="text-xs text-gray-500">{c.claimant_email}</div>
                                  )}
                                </td>
                                <td className="py-2 text-gray-600">{c.business_name || '-'}</td>
                                <td className="py-2 text-gray-600">{c.claimant_phone || '-'}</td>
                                <td className="py-2">
                                  <div className="text-gray-900">{c.event_type || '-'}</div>
                                  {c.event_date && (
                                    <div className="text-xs text-gray-500">{c.event_date}</div>
                                  )}
                                </td>
                                <td className="py-2">
                                  {getStatusBadge(c.status)}
                                  {c.source === 'bulk_import' && (
                                    <span className="ml-1 text-[10px] text-gray-400">(imported)</span>
                                  )}
                                </td>
                                <td className="py-2 text-gray-500">
                                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(firm.clients_total || firm.clients.length) > 15 && (
                          <p className="text-xs text-gray-500 text-center pt-2">
                            Showing 15 of {firm.clients_total || firm.clients.length} clients
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No clients yet</p>
                        <button
                          onClick={() => openBulkImport(firm)}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                        >
                          Import clients
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{lawFirms.length} law firm{lawFirms.length !== 1 ? 's' : ''} total</span>
            <span>{lawFirms.reduce((sum, f) => sum + f.cases_count, 0)} total cases</span>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">
                  {editingFirm ? 'Edit Law Firm' : 'Add New Law Firm'}
                </h3>
                <button onClick={handleCloseModal} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {validationError && (
                <p className="text-red-500 text-sm">{validationError}</p>
              )}
              {successMessage && (
                <p className="text-green-600 text-sm">{successMessage}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Law Firm Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setValidationError(null); }}
                  placeholder="e.g., Smith & Associates LLP"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Primary contact"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="email@lawfirm.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="City, State"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingFirm ? 'Save Changes' : 'Add Law Firm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && bulkImportFirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Bulk Import Clients</h3>
                  <p className="text-sm text-green-100">Import multiple clients for {bulkImportFirm.name}</p>
                </div>
                <button 
                  onClick={() => { setShowBulkImportModal(false); setBulkImportFirm(null); setBulkImportData(''); }}
                  className="text-white/80 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-2">CSV Format (one client per line):</p>
                <code className="text-xs text-blue-700 block bg-blue-100 p-2 rounded font-mono">
                  claimant_name, email, phone, business_name, address, event, event_date
                </code>
                <p className="text-xs text-blue-600 mt-2">
                  Example: John Smith, john@email.com, 555-1234, Pacific Surf Shop, 123 Beach Blvd, Fire, 2024-06-15
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  <strong>Required:</strong> claimant_name (first column). All other fields are optional.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste CSV Data ({bulkImportData.split('\n').filter(l => l.trim()).length} clients detected)
                </label>
                <textarea
                  value={bulkImportData}
                  onChange={(e) => setBulkImportData(e.target.value)}
                  placeholder="John Smith, john@email.com, 555-1234, Pacific Surf Shop, 123 Beach Blvd, Fire, 2024-06-15&#10;Jane Doe, jane@email.com, 555-5678, Joe's Diner, 456 Main St, Hurricane, 2024-07-20&#10;Bob Wilson, bob@email.com, , Corner Store, 789 Oak Ave, ,&#10;..."
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                  data-testid="bulk-import-textarea"
                />
              </div>
              
              <div className="mt-3 flex items-start gap-2 text-xs text-gray-500">
                <span className="inline-block w-4 h-4 rounded-full bg-green-100 text-green-600 flex-shrink-0 flex items-center justify-center mt-0.5">✓</span>
                <span>Supports up to 1,000 clients per import. Copy/paste directly from Excel or Google Sheets.</span>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {bulkImportData.split('\n').filter(l => l.trim()).length} clients ready to import
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowBulkImportModal(false); setBulkImportFirm(null); setBulkImportData(''); }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                  data-testid="bulk-import-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={bulkImporting || !bulkImportData.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="bulk-import-submit"
                >
                  {bulkImporting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Import Clients
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attorney Password Modal */}
      {showPasswordModal && passwordFirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Scale className="w-5 h-5 text-white" />
                  <h3 className="text-lg font-bold text-white">
                    {passwordFirm.attorney_password_set ? 'Update' : 'Set'} Attorney Portal Access
                  </h3>
                </div>
                <button onClick={() => { setShowPasswordModal(false); setPasswordFirm(null); setAttorneyPassword(''); }} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>{passwordFirm.name}</strong> will be able to access their released cases at:
                </p>
                <p className="text-sm font-mono text-blue-600 mt-1">
                  {window.location.origin}/attorney/login
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Login Email
                </label>
                <input
                  type="text"
                  value={passwordFirm.contact_email || 'No email set'}
                  disabled
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
                {!passwordFirm.contact_email && (
                  <p className="text-xs text-red-500 mt-1">Please add a contact email to this law firm first.</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={attorneyPassword}
                  onChange={(e) => setAttorneyPassword(e.target.value)}
                  placeholder="Set a secure password"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="attorney-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This password will be used by the attorney to access their portal.
                </p>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => { setShowPasswordModal(false); setPasswordFirm(null); setAttorneyPassword(''); }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSetAttorneyPassword}
                disabled={settingPassword || !attorneyPassword || !passwordFirm.contact_email}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="set-attorney-password-button"
              >
                {settingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                {passwordFirm.attorney_password_set ? 'Update Password' : 'Enable Portal Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
